/**
 * Audit trail.
 *
 * Read-only by construction: the table is append-only in the database, so there
 * is nothing here to edit. This is the screen EGBA opens when someone asks
 * "who changed this price, and when".
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type EventoAuditoria, type Lista } from '../api/cliente'
import {
  Cabecalho,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'

const ENTIDADES = [
  { valor: '', texto: 'Todas as entidades' },
  { valor: 'core.categorias_publicacao', texto: 'Categorias' },
  { valor: 'core.tabela_precos', texto: 'Tabelas de preço' },
  { valor: 'core.configuracoes', texto: 'Configurações' },
]

const TOM_ACAO: Record<string, 'sucesso' | 'marca' | 'atencao' | 'neutro'> = {
  criar: 'sucesso',
  editar: 'marca',
  desativar: 'atencao',
}

const NOME_ENTIDADE: Record<string, string> = {
  'core.categorias_publicacao': 'Categoria',
  'core.tabela_precos': 'Tabela de preço',
  'core.configuracoes': 'Configuração',
}

export function Auditoria() {
  const [entidade, setEntidade] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['auditoria', entidade],
    queryFn: () =>
      api.get<Lista<EventoAuditoria>>(
        `/api/v1/admin/auditoria?limite=100${entidade ? `&entidade=${entidade}` : ''}`,
      ),
  })

  const eventos = data?.dados ?? []

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Trilha de auditoria"
        descricao="Registro imutável de toda alteração em categorias, preços e configurações."
        acoes={
          <div className="w-56">
            <Selecao
              rotulo=""
              value={entidade}
              onChange={(e) => setEntidade(e.target.value)}
              opcoes={ENTIDADES}
            />
          </div>
        }
      >
        {isLoading ? (
          <Carregando />
        ) : eventos.length === 0 ? (
          <Vazio
            titulo="Nenhum registro"
            descricao="As alterações feitas no painel aparecerão aqui automaticamente."
          />
        ) : (
          <Tabela>
            <Cabecalho colunas={['Quando', 'Ação', 'Entidade', 'Registro']} />
            <tbody className="divide-y divide-slate-100">
              {eventos.map((e) => (
                <tr key={e.id} className="transition hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 tabular">
                    {formatarData(e.ocorridoEm)}
                  </td>
                  <td className="px-4 py-3">
                    <Etiqueta tom={TOM_ACAO[e.acao] ?? 'neutro'}>{e.acao}</Etiqueta>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {NOME_ENTIDADE[e.entidade] ?? e.entidade}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">
                    {e.entidadeId ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>
    </div>
  )
}
